/**
 * The ONE saved-lead client for the whole app.
 *
 * Every surface (Auto Sourcing, Product Analyzer, Saved Leads, the product
 * popup, the sidebar count) must go through these functions, so there is a
 * single authoritative saved state instead of per-page implementations.
 *
 * Failure policy: these throw. Callers must only mark a card "saved" AFTER the
 * promise resolves — the previous Auto Sourcing bug was that the bookmark called
 * a no-op and nothing was ever persisted.
 */
import { del, get, post } from "@/lib/api";

const BASE = "/storefront-hunter/saved-products";

/**
 * Login-scoped stable identity for one saved lead.
 *
 * Mirrors the backend: `source_key` is `<source_id>:<source_product_id>` for a
 * retailer offer, and empty for a bare Walmart bookmark. One retailer offer is a
 * distinct lead from another retailer's offer even when both point at the same
 * Walmart item.
 */
export function savedLeadKey({ walmartItemId, sourceId, sourceProductId } = {}) {
  const itemId = String(walmartItemId ?? "").trim();
  if (!itemId) return "";
  const src = String(sourceId ?? "").trim();
  const pid = String(sourceProductId ?? "").trim();
  const sourceKey = src && pid ? `${src}:${pid}` : "";
  return sourceKey ? `${itemId}__${sourceKey}` : itemId;
}

/** Saved-state key from a saved-lead row returned by the API. */
export function savedKeyFromLead(lead) {
  return savedLeadKey({
    walmartItemId: lead?.walmartItemId ?? lead?.walmart_item_id ?? lead?.item_id,
    sourceId: lead?.sourceId ?? lead?.source_id,
    sourceProductId: lead?.sourceProductId ?? lead?.source_product_id,
  });
}

/** Build the save payload from a product-comparison lead (pure; testable). */
export function savePayloadFromLead(lead) {
  const itemId = String(lead?.walmartItemId ?? lead?.walmart_item_id ?? "").trim();
  const sourceId = lead?.sourceId ?? lead?.source_id ?? null;
  const sourceProductId = lead?.sourceProductId ?? lead?.source_product_id ?? null;
  const sourceKey = sourceId && sourceProductId ? `${sourceId}:${sourceProductId}` : null;
  return {
    walmart_item_id: itemId,
    title: lead?.walmartTitle || lead?.sourceTitle || null,
    image_url: lead?.walmartImage || null,
    brand: lead?.walmartBrand || null,
    category: lead?.walmartCategory || null,
    // Source side of the pair, preserved so the lead re-renders as a comparison.
    source_key: sourceKey,
    source_id: sourceId,
    source_product_id: sourceProductId,
    source_title: lead?.sourceTitle || null,
    source_brand: lead?.sourceBrand || null,
    source_url: lead?.sourceUrl || null,
    source_image: lead?.sourceImage || null,
    source_price: lead?.sourcePrice ?? null,
    // The Walmart sell price was never part of the payload, so it could not be
    // persisted and every saved card rendered a dash for it.
    walmart_price: lead?.walmartPrice ?? null,
    walmart_url: lead?.walmartUrl || null,
    walmart_title: lead?.walmartTitle || null,
    walmart_brand: lead?.walmartBrand || null,
    match_state: lead?.matchState || null,
    match_confidence: lead?.matchConfidence ?? null,
    qualification: lead?.qualification || null,
    run_id: lead?.runId || null,
    estimated_profit: lead?.profit ?? null,
    estimated_roi: lead?.roi ?? null,
    estimated_margin: lead?.margin ?? null,
  };
}

export const listSavedLeads = (limit = 500) =>
  get(`${BASE}?limit=${encodeURIComponent(limit)}`);

/** Batch saved-state — one request for many keys, never N+1. */
export const fetchSavedStatus = (itemIds) => post(`${BASE}/status`, { item_ids: itemIds });

/** Persist a lead. Idempotent server-side; throws on failure. */
export const saveLead = (lead) => {
  const payload = savePayloadFromLead(lead);
  if (!payload.walmart_item_id) {
    return Promise.reject(new Error("This product has no matched Walmart listing yet."));
  }
  return post(BASE, payload);
};

export const unsaveLead = ({ walmartItemId, sourceKey }) => {
  const itemId = String(walmartItemId ?? "").trim();
  if (!itemId) return Promise.reject(new Error("Missing Walmart item id."));
  const qs = sourceKey ? `?source_key=${encodeURIComponent(sourceKey)}` : "";
  return del(`${BASE}/${encodeURIComponent(itemId)}${qs}`);
};
